import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import enCommon from '../locales/en/common.json';
import enVoice from '../locales/en/voice.json';
import enSettings from '../locales/en/settings.json';
import enAdhd from '../locales/en/adhd.json';
import enTasks from '../locales/en/tasks.json';

import esCommon from '../locales/es/common.json';
import esVoice from '../locales/es/voice.json';
import esSettings from '../locales/es/settings.json';
import esAdhd from '../locales/es/adhd.json';
import esTasks from '../locales/es/tasks.json';

import zhCommon from '../locales/zh/common.json';
import zhVoice from '../locales/zh/voice.json';
import zhSettings from '../locales/zh/settings.json';
import zhAdhd from '../locales/zh/adhd.json';
import zhTasks from '../locales/zh/tasks.json';

import ptCommon from '../locales/pt/common.json';
import ptVoice from '../locales/pt/voice.json';
import ptSettings from '../locales/pt/settings.json';
import ptAdhd from '../locales/pt/adhd.json';
import ptTasks from '../locales/pt/tasks.json';

import frCommon from '../locales/fr/common.json';
import frVoice from '../locales/fr/voice.json';
import frSettings from '../locales/fr/settings.json';
import frAdhd from '../locales/fr/adhd.json';
import frTasks from '../locales/fr/tasks.json';

import arCommon from '../locales/ar/common.json';
import arVoice from '../locales/ar/voice.json';
import arSettings from '../locales/ar/settings.json';
import arAdhd from '../locales/ar/adhd.json';
import arTasks from '../locales/ar/tasks.json';

import hiCommon from '../locales/hi/common.json';
import hiVoice from '../locales/hi/voice.json';
import hiSettings from '../locales/hi/settings.json';
import hiAdhd from '../locales/hi/adhd.json';
import hiTasks from '../locales/hi/tasks.json';

import deCommon from '../locales/de/common.json';
import deVoice from '../locales/de/voice.json';
import deSettings from '../locales/de/settings.json';
import deAdhd from '../locales/de/adhd.json';
import deTasks from '../locales/de/tasks.json';

// Language resources
const resources = {
  en: {
    common: enCommon,
    voice: enVoice,
    settings: enSettings,
    adhd: enAdhd,
    tasks: enTasks,
  },
  es: {
    common: esCommon,
    voice: esVoice,
    settings: esSettings,
    adhd: esAdhd,
    tasks: esTasks,
  },
  zh: {
    common: zhCommon,
    voice: zhVoice,
    settings: zhSettings,
    adhd: zhAdhd,
    tasks: zhTasks,
  },
  pt: {
    common: ptCommon,
    voice: ptVoice,
    settings: ptSettings,
    adhd: ptAdhd,
    tasks: ptTasks,
  },
  fr: {
    common: frCommon,
    voice: frVoice,
    settings: frSettings,
    adhd: frAdhd,
    tasks: frTasks,
  },
  ar: {
    common: arCommon,
    voice: arVoice,
    settings: arSettings,
    adhd: arAdhd,
    tasks: arTasks,
  },
  hi: {
    common: hiCommon,
    voice: hiVoice,
    settings: hiSettings,
    adhd: hiAdhd,
    tasks: hiTasks,
  },
  de: {
    common: deCommon,
    voice: deVoice,
    settings: deSettings,
    adhd: deAdhd,
    tasks: deTasks,
  },
};

// Supported languages configuration
export const supportedLanguages = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
];

// Get language code from full locale (e.g., 'en-US' -> 'en')
const getLanguageCode = (locale: string): string => {
  const code = locale.split('-')[0];
  return supportedLanguages.some(lang => lang.code === code) ? code : 'en';
};

// Custom language detector configuration
const languageDetector = new LanguageDetector();
languageDetector.addDetector({
  name: 'flowcusSettings',
  lookup: () => {
    // Check localStorage for saved language preference
    const savedLanguage = localStorage.getItem('flowcus-language');
    if (savedLanguage && supportedLanguages.some(lang => lang.code === savedLanguage)) {
      return savedLanguage;
    }
    return undefined;
  },
  cacheUserLanguage: (language: string) => {
    // Save language preference to localStorage
    localStorage.setItem('flowcus-language', getLanguageCode(language));
  },
});

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: ['common', 'voice', 'settings', 'adhd', 'tasks'],
    
    // Language detection configuration
    detection: {
      order: ['flowcusSettings', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'flowcus-language',
      caches: ['flowcusSettings'],
    },

    // Interpolation configuration
    interpolation: {
      escapeValue: false, // React already does escaping
    },

    // Development configuration
    debug: process.env.NODE_ENV === 'development',
    
    // Ensure we only use supported languages
    supportedLngs: supportedLanguages.map(lang => lang.code),
    nonExplicitSupportedLngs: true,
    
    // Key separator configuration
    keySeparator: '.',
    nsSeparator: ':',
  });

// Helper function to change language
export const changeLanguage = (languageCode: string): Promise<any> => {
  const code = getLanguageCode(languageCode);
  return i18n.changeLanguage(code);
};

// Helper function to get current language info
export const getCurrentLanguageInfo = () => {
  const currentLang = i18n.language;
  const code = getLanguageCode(currentLang);
  return supportedLanguages.find(lang => lang.code === code) || supportedLanguages[0];
};

export default i18n;